import React, { useState, useEffect } from 'react';
import './CandidateDecisionHeader.css';

/**
 * Premium Candidate Decision Header Component
 * 
 * Displays candidate identity, hero score with radial ring, status, signals, and actions.
 * Designed for executive decision-making with world-class visual hierarchy.
 */
const CandidateDecisionHeader = ({
    candidateName,
    roleTitle,
    jobTitle,
    companyName,
    calibratedScore,
    statusLabel,
    confidence,
    benchmarkPercentile,
    evidenceQuality,
    onScheduleInterview,
    onPhoneScreening,
    onDownloadJD,
    onViewResume,
    onDownloadAnalysis,
    onConductInterview,
    onEvaluateInterview,
    hasQuestionnaire = false,
    isGeneratingQuestions = false,
    phoneScreeningCompleted = false,
    scoreSource = 'JD-weighted',
    lastEvaluated,
    evaluationMode
}) => {
    const [scoreAnimation, setScoreAnimation] = useState(0);

    // Animate score ring on mount
    useEffect(() => {
        if (calibratedScore !== undefined && calibratedScore !== null) {
            const timer = setTimeout(() => {
                setScoreAnimation(calibratedScore);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [calibratedScore]);

    // Calculate stroke-dashoffset for radial ring
    const radius = 60;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (scoreAnimation / 100) * circumference;

    // Get status pill styling
    const getStatusPillStyle = (status) => {
        const statusLower = (status || '').toLowerCase();
        if (statusLower.includes('eligible') || statusLower.includes('book the call') || statusLower.includes('proceed')) {
            return 'status-eligible';
        } else if (statusLower.includes('caution') || statusLower.includes('hold') || statusLower.includes('review')) {
            return 'status-caution';
        } else if (statusLower.includes('not') || statusLower.includes('reject') || statusLower.includes('no')) {
            return 'status-not-recommended';
        }
        return 'status-default';
    };

    // Format value or show dash
    const formatValue = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '—';
        if (typeof value === 'number') {
            return value.toFixed(1);
        }
        return value;
    };

    // Mini Metric Component with Tooltip
    const MiniMetric = ({ label, value, tooltip }) => {
        const [showTooltip, setShowTooltip] = useState(false);
        
        return (
            <div 
                className="mini-metric"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
            >
                <div className="mini-metric-label">{label}</div>
                <div className="mini-metric-value">{formatValue(value)}{typeof value === 'number' && label === 'Confidence' ? '%' : label === 'Evidence' ? '%' : label === 'Benchmark' ? '%' : ''}</div>
                {tooltip && (
                    <div className={`mini-metric-tooltip ${showTooltip ? 'visible' : ''}`}>
                        {tooltip}
                    </div>
                )}
                <span className="mini-metric-icon">ⓘ</span>
            </div>
        );
    };

    return (
        <div className="candidate-decision-header">
            {/* Zone A: Left - Identity */}
            <div className="header-zone-identity">
                <h1 className="candidate-name-hero">{candidateName || '—'}</h1>
                <div className="role-title">{roleTitle || jobTitle || '—'}</div>
                <div className="identity-metadata">
                    {scoreSource && (
                        <span className="metadata-chip">{scoreSource}</span>
                    )}
                    {lastEvaluated && (
                        <span className="metadata-chip">
                            {new Date(lastEvaluated).toLocaleDateString()}
                        </span>
                    )}
                    {evaluationMode && (
                        <span className="metadata-chip">{evaluationMode}</span>
                    )}
                </div>
            </div>

            {/* Zone B: Center - Hero Score */}
            <div className="header-zone-score">
                <div className="score-ring-container">
                    <svg className="score-ring-svg" viewBox="0 0 140 140">
                        {/* Background circle */}
                        <circle
                            cx="70"
                            cy="70"
                            r={radius}
                            fill="none"
                            stroke="rgba(255, 255, 255, 0.1)"
                            strokeWidth="8"
                        />
                        {/* Score circle */}
                        <circle
                            cx="70"
                            cy="70"
                            r={radius}
                            fill="none"
                            stroke="url(#scoreGradient)"
                            strokeWidth="8"
                            strokeLinecap="round"
                            strokeDasharray={circumference}
                            strokeDashoffset={offset}
                            className="score-ring-circle"
                            transform="rotate(-90 70 70)"
                        />
                        <defs>
                            <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#00a6f4" />
                                <stop offset="100%" stopColor="#8b4ad2" />
                            </linearGradient>
                        </defs>
                    </svg>
                    <div className="score-ring-content">
                        <div className="score-number-hero">
                            {calibratedScore !== null && calibratedScore !== undefined 
                                ? Math.round(calibratedScore) 
                                : '—'}
                        </div>
                        <div className="score-label-hero">Calibrated Score</div>
                    </div>
                </div>

                {/* Status Pill */}
                {statusLabel && (
                    <div className={`status-pill ${getStatusPillStyle(statusLabel)}`}>
                        {statusLabel}
                    </div>
                )}

                {/* Signal Row */}
                <div className="signal-row">
                    <MiniMetric
                        label="Confidence"
                        value={confidence !== undefined && confidence !== null ? confidence : undefined}
                        tooltip="Evidence-based reliability for this evaluation."
                    />
                    <MiniMetric
                        label="Benchmark"
                        value={benchmarkPercentile !== undefined && benchmarkPercentile !== null ? benchmarkPercentile : undefined}
                        tooltip="Percentile vs historical candidates for this role."
                    />
                    <MiniMetric
                        label="Evidence"
                        value={evidenceQuality !== undefined && evidenceQuality !== null ? evidenceQuality : undefined}
                        tooltip="Quality of citations, extraction, and requirement clarity."
                    />
                </div>
            </div>

            {/* Zone C: Right - Actions */}
            <div className="header-zone-actions">
                {onPhoneScreening && (
                    <button
                        className="action-btn action-primary"
                        onClick={onPhoneScreening}
                    >
                        {phoneScreeningCompleted ? 'View Recruiter Screening' : 'Recruiter Screening'}
                    </button>
                )}
                <button
                    className={`action-btn ${onPhoneScreening ? 'action-secondary' : 'action-primary'}`}
                    onClick={onScheduleInterview}
                >
                    Schedule Interview
                </button>
                <button
                    className="action-btn action-secondary"
                    onClick={onDownloadJD}
                    disabled={!onDownloadJD}
                >
                    Download JD
                </button>
                <button
                    className="action-btn action-secondary"
                    onClick={onViewResume}
                >
                    View Resume
                </button>
                <button
                    className="action-btn action-secondary"
                    onClick={onDownloadAnalysis}
                >
                    Download Analysis
                </button>
                {(onConductInterview || onEvaluateInterview) && (
                    <button
                        className="action-btn action-secondary"
                        onClick={hasQuestionnaire && onEvaluateInterview ? onEvaluateInterview : onConductInterview}
                        disabled={isGeneratingQuestions}
                    >
                        {isGeneratingQuestions ? 'Generating Questions...' : hasQuestionnaire ? 'Evaluate Interview' : 'Conduct Interview'}
                    </button>
                )}
            </div>
        </div>
    );
};

export default CandidateDecisionHeader;
